<template>
  <div
    v-observe-visibility="onVisibilityChanged"
    class="testimonials"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <div class="body">
      <div class="slider glide">
        <div
          class="list-columns glide__track"
          data-glide-el="track"
        >
          <div class="glide__slides">
            <MoleculeTestimonial
              v-for="(testimonial, key) in testimonials"
              :key="key"
              class="glide__slide"
              :testimonial="testimonial"
              :with-stars="withStars"
            >
              <slot name="testimonial" />
            </MoleculeTestimonial>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="testimonialCount > 1 || to"
      class="footer"
      :class="{ 'with-arrows': useNavigationWithArrows }"
    >
      <div
        v-if="useNavigationWithArrows"
        class="navigation"
      >
        <UiComponentIcon
          type="arrow-left"
          @click="glide.go('<')"
        />
        <UiComponentIcon
          type="arrow-right"
          @click="glide.go('>')"
        />
      </div>
      <div
        v-else-if="testimonialCount > 1"
        class="navigation"
      >
        <div
          v-for="n in testimonialCount"
          :key="n"
          class="bullet"
          :class="{ active: index === n - 1 }"
          @click="setIndex(n - 1)"
        />
      </div>
      <AtomButton
        v-if="to"
        :to="to"
        type="text"
        @click="emit('button:click', $event)"
      >
        <slot name="call-to-action" />
        <UiComponentIcon type="arrow-right" />
      </AtomButton>
    </div>
  </div>
</template>

<script setup>
import Glide from '@glidejs/glide'

// Directives
const { vObserveVisibility } = useVObserveVisibility()

// Emitter
const emit = defineEmits([
  'button:click',
])

// Props
const props = defineProps({
  duration: {
    default() {
      return 5000
    },
    required: false,
    type: Number,
  },
  isAnimated: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  testimonials: {
    required: true,
    type: Array,
  },
  to: {
    default() {
      return undefined
    },
    required: false,
    type: String,
  },
  useNavigationWithArrows: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
  withStars: {
    default() {
      return false
    },
    required: false,
    type: Boolean,
  },
})

// Refs
const glide = ref(null)
const index = ref(0)
const timeoutId = ref(null)

// Computed Properties
const testimonialCount = computed(() => {
  return props.testimonials.length
})

// Methods
function onMouseEnter() {
  stopTimeout()
}

function onMouseLeave() {
  if (props.isAnimated) {
    startTimeout()
  }
}

function onVisibilityChanged(isVisible) {
  if (props.isAnimated) {
    if (isVisible) {
      startTimeout()
    }
    else {
      stopTimeout()
    }
  }
}

function setIndex(value) {
  index.value = value
  glide.value.go(`=${value}`)
}

function slideToNextItem() {
  stopTimeout()
  glide.value.go('>')
  startTimeout()
}

function slideChange() {
  index.value = glide.value.index
}

function startTimeout() {
  timeoutId.value = window.setTimeout(
    slideToNextItem,
    props.duration,
  )
}

function stopTimeout() {
  if (timeoutId.value) {
    window.clearTimeout(timeoutId.value)
    timeoutId.value = null
  }
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  glide.value.destroy()

  if (timeoutId.value) {
    window.clearTimeout(timeoutId.value)
  }
})

onMounted(() => {
  glide.value = new Glide('.testimonials .glide', {
    type: 'slider',
    gap: 0,
    perView: 1,
  })
  glide.value.on('run.after', slideChange)
  glide.value.mount()
})
</script>

<style lang="postcss" scoped>
@import "../../../assets/css/glide.css";

.testimonials {
  @apply relative flex flex-col w-full;

  @screen xl {
    @apply flex-grow;
  }

  .body {
    @apply bg-white w-full border-grey-blue-200 rounded-4xl overflow-hidden cursor-move;
    border-width: 3px;

    .slider {
      @apply flex flex-row transform transition duration-500 ease-in-out;
    }
  }

  .footer {
    @apply flex flex-row w-full justify-between items-center mt-2;

    @screen xl {
      bottom: -48px;
      @apply absolute;
    }

    &.with-arrows {
      @screen xl {
        @apply mt-0;
        bottom: -84px;
      }

      .navigation {
        @apply items-center;
        height: 84px;
      }
    }

    .navigation {
      @apply flex flex-row;

      .bullet {
        @apply w-2 h-2 ml-1 mr-1 rounded-full bg-livestorm-blue-300 cursor-pointer transition duration-500 ease-out;

        &.active {
          @apply bg-livestorm-blue-500 cursor-default;
        }

        &:first-child {
          @apply ml-0;
        }

        &:last-child {
          @apply mr-0;
        }
      }

      i {
        @apply text-xl cursor-pointer;

        &:first-child {
          @apply mr-4;
        }
      }
    }

    .button {
      @apply ml-auto;
    }
  }
}
</style>
